import { AuthCard } from "@/components/auth/AuthCard";
import { GoogleSignInButton } from "@/components/auth/GoogleSignInButton";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Spinner } from "@/components/ui/spinner";
import { authClient } from "@/lib/auth/client";
import { getClientConfig } from "@/lib/client-config";
import { isValidEmailAddress } from "@/lib/validation/email";
import { Eye, EyeOff, Lock, Mail, RotateCcw } from "lucide-react";
import * as React from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";

export default function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation();

  const [email, setEmail] = React.useState("");
  const [password, setPassword] = React.useState("");
  const [showPassword, setShowPassword] = React.useState(false);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [resendLoading, setResendLoading] = React.useState(false);
  const [resendCooldown, setResendCooldown] = React.useState(0);
  const [resendSuccess, setResendSuccess] = React.useState<string | null>(null);
  const [googleOAuthEnabled, setGoogleOAuthEnabled] = React.useState(false);

  const infoMessage = (location.state as { message?: string } | null)?.message;
  const redirectAfterLogin = React.useMemo(() => {
    const value = new URLSearchParams(location.search).get("redirect");
    return value && value.startsWith("/") && !value.startsWith("//") ? value : "/";
  }, [location.search]);
  const signupPath = `/auth/signup${location.search}`;
  const isEmailValid = isValidEmailAddress(email);

  const isEmailNotVerifiedError = error?.toLowerCase().includes("not verified") || false;

  React.useEffect(() => {
    let cancelled = false;
    void getClientConfig().then((config) => {
      if (!cancelled) setGoogleOAuthEnabled(config.auth.google);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  React.useEffect(() => {
    if (resendCooldown <= 0) return;
    const timer = setInterval(() => {
      setResendCooldown((prev) => prev - 1);
    }, 1000);
    return () => clearInterval(timer);
  }, [resendCooldown]);

  const handleResendVerification = async () => {
    if (!isEmailValid || resendCooldown > 0 || resendLoading) return;
    setResendLoading(true);
    setResendSuccess(null);
    setError(null);
    try {
      const result = await authClient.sendVerificationEmail({
        email: email.trim(),
        callbackURL: "/auth/login",
      });
      if (result?.error) {
        setError(result.error.message ?? "Unable to resend verification email.");
        return;
      }
      setResendSuccess("Verification email resent. Check your inbox.");
      setResendCooldown(60);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Unable to resend verification email.");
    } finally {
      setResendLoading(false);
    }
  };

  const onSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!isEmailValid) {
      setError("Please enter a valid email address.");
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const result = await authClient.signIn.email({
        email: email.trim(),
        password,
        rememberMe: true,
      });

      if (result?.error) {
        setError(result.error.message ?? "Unable to sign in.");
        return;
      }

      const response = result?.data as { twoFactorRedirect?: boolean } | undefined;
      if (response?.twoFactorRedirect) {
        navigate("/auth/2fa", { replace: true });
        return;
      }

      await authClient.getSession();
      if (redirectAfterLogin.startsWith("/api/")) {
        window.location.assign(redirectAfterLogin);
        return;
      }
      navigate(redirectAfterLogin, { replace: true });
    } catch (submitError) {
      setError(submitError instanceof Error ? submitError.message : "Unable to sign in.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthCard
      title="Welcome back"
      description="Sign in to your account to continue."
      footer={
        <p>
          Don&apos;t have an account?{" "}
          <Link className="text-primary font-medium hover:underline" to={signupPath}>
            Create one
          </Link>
        </p>
      }
    >
      {googleOAuthEnabled ? (
        <div className="mb-4 space-y-4">
          <GoogleSignInButton
            callbackURL={redirectAfterLogin}
            label="Continue with Google"
            onError={setError}
          />
          <div className="relative text-center text-xs text-muted-foreground">
            <span className="relative z-10 bg-card px-2">or sign in with email</span>
            <span className="absolute left-0 top-1/2 h-px w-full bg-border" />
          </div>
        </div>
      ) : null}

      <form className="space-y-4" onSubmit={onSubmit}>
        <div className="space-y-2">
          <Label htmlFor="email">Email</Label>
          <div className="relative">
            <Mail className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-muted-foreground" />
            <Input
              id="email"
              type="email"
              placeholder="you@company.com"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              autoComplete="email"
              required
              className="pl-9"
            />
          </div>
        </div>

        <div className="space-y-2">
          <Label htmlFor="password">Password</Label>
          <div className="relative">
            <Lock className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-muted-foreground" />
            <Input
              id="password"
              type={showPassword ? "text" : "password"}
              placeholder="Enter your password"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              autoComplete="current-password"
              required
              className="pl-9 pr-9"
            />
            <button
              type="button"
              tabIndex={-1}
              aria-label={showPassword ? "Hide password" : "Show password"}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
              onClick={() => setShowPassword((v) => !v)}
            >
              {showPassword ? <EyeOff className="size-4" /> : <Eye className="size-4" />}
            </button>
          </div>
        </div>

        {infoMessage ? (
          <div role="alert" aria-live="polite" className="rounded-md bg-emerald-50 border border-emerald-200 px-3 py-2 text-sm text-emerald-700 dark:bg-emerald-950/30 dark:border-emerald-800/50 dark:text-emerald-300 space-y-2">
            <p>{infoMessage}</p>
            {isEmailValid && (
              <button
                type="button"
                onClick={handleResendVerification}
                disabled={resendLoading || resendCooldown > 0}
                className="inline-flex items-center gap-1.5 text-xs font-medium text-emerald-800 hover:text-emerald-900 dark:text-emerald-300 dark:hover:text-emerald-200 disabled:opacity-50 disabled:cursor-not-allowed transition-colors underline underline-offset-2"
              >
                {resendLoading ? (
                  <>
                    <Spinner size="sm" className="text-emerald-700 dark:text-emerald-300" />
                    Sending...
                  </>
                ) : resendCooldown > 0 ? (
                  <>
                    <RotateCcw className="size-3" />
                    Resend in {resendCooldown}s
                  </>
                ) : (
                  <>
                    <RotateCcw className="size-3" />
                    Resend verification email
                  </>
                )}
              </button>
            )}
          </div>
        ) : null}
        {resendSuccess ? (
          <p role="alert" aria-live="polite" className="rounded-md bg-emerald-50 border border-emerald-200 px-3 py-2 text-sm text-emerald-700 dark:bg-emerald-950/30 dark:border-emerald-800/50 dark:text-emerald-300">
            {resendSuccess}
          </p>
        ) : null}
        {error && isEmailNotVerifiedError ? (
          <div role="alert" aria-live="polite" className="rounded-md bg-amber-50 border border-amber-200 px-3 py-2 text-sm text-amber-700 dark:bg-amber-950/30 dark:border-amber-800/50 dark:text-amber-300 space-y-2">
            <p>{error}</p>
            {isEmailValid && (
              <button
                type="button"
                onClick={handleResendVerification}
                disabled={resendLoading || resendCooldown > 0}
                className="inline-flex items-center gap-1.5 text-xs font-medium text-amber-800 hover:text-amber-900 dark:text-amber-300 dark:hover:text-amber-200 disabled:opacity-50 disabled:cursor-not-allowed transition-colors underline underline-offset-2"
              >
                {resendLoading ? (
                  <>
                    <Spinner size="sm" className="text-amber-700 dark:text-amber-300" />
                    Sending...
                  </>
                ) : resendCooldown > 0 ? (
                  <>
                    <RotateCcw className="size-3" />
                    Resend in {resendCooldown}s
                  </>
                ) : (
                  <>
                    <RotateCcw className="size-3" />
                    Resend verification email
                  </>
                )}
              </button>
            )}
          </div>
        ) : null}
        {error && !isEmailNotVerifiedError ? (
          <p role="alert" aria-live="polite" className="rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">
            {error}
          </p>
        ) : null}

        <div className="flex items-center justify-between gap-2 pt-1">
          <Link className="text-sm text-muted-foreground hover:text-primary transition-colors" to="/auth/forgot-password">
            Forgot password?
          </Link>
          <Button type="submit" disabled={loading || !isEmailValid || password.length < 8}>
            {loading ? <Spinner size="sm" className="text-primary-foreground" /> : null}
            <span translate="no">{loading ? "Signing in..." : "Sign in"}</span>
          </Button>
        </div>
      </form>
    </AuthCard>
  );
}
